import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
export interface FeaturePageProps {
  seoTitle?: string;
  seoDesc?: string;
  heroEyebrow?: string;
  heroTitle?: React.ReactNode;
  heroDesc?: string;
  heroImage?: string;
  hero?: {
    badge?: string;
    h1?: React.ReactNode;
    subtitle?: string;
    stats?: {
      value: string;
      label: string;
    }[];
  };
  customContent?: React.ReactNode;
  problem?: {
    subtitle: string;
    cards: {
      icon: React.ReactNode;
      title: string;
      desc: string;
    }[];
  };
  howItWorks?: {
    title?: string;
    steps: {
      title: string;
      desc: string;
    }[];
  };
  features?: {
    cards: {
      icon: React.ReactNode;
      title: string;
      desc: string;
    }[];
  };
  integrations?: {
    tools: string[];
  };
  pricingCallout?: string;
  demoCallout?: string;
  demoTitle?: string;
  demoDesc?: string;
  firstClientCallout?: string;
}
function AnimatedCounter({
  value
}: {
  value: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  const [displayValue, setDisplayValue] = useState('0');
  useEffect(() => {
    if (!isInView) return;
    // If value contains non-numeric characters, just show it
    const numericMatch = value.match(/[\d.]+/);
    if (!numericMatch) {
      setDisplayValue(value);
      return;
    }
    const target = parseFloat(numericMatch[0]);
    const suffix = value.replace(numericMatch[0], '');
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      current += increment;
      if (step >= steps) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current) + suffix);
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, value]);
  return <div ref={ref} className="font-poppins font-extrabold text-4xl md:text-5xl text-white" data-id="element-123">
      {displayValue}
    </div>;
}
export function FeaturePageLayout({
  hero,
  heroEyebrow,
  heroTitle,
  heroDesc,
  heroImage,
  seoTitle,
  seoDesc,
  customContent,
  problem,
  howItWorks,
  features,
  integrations,
  pricingCallout,
  demoCallout,
  demoTitle,
  demoDesc,
  firstClientCallout
}: FeaturePageProps) {
  // Support both flat props and hero object
  const badge = heroEyebrow || hero?.badge;
  const h1 = heroTitle || hero?.h1;
  const subtitle = heroDesc || hero?.subtitle;
  const stats = hero?.stats;
  return <main className="pt-20" data-id="element-124">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 lg:pt-44 lg:pb-36 px-6 bg-hero-gradient overflow-hidden" data-id="element-125">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30" data-id="element-126" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20" data-id="element-127" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-128" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-129">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-130">
            {badge && <div className="mb-6 flex justify-center" data-id="element-131">
                <span className="inline-flex items-center px-5 py-2 rounded-full border border-purple/40 bg-purple/10 text-sm font-poppins font-semibold tracking-wider uppercase text-purple-300" data-id="element-132">
                  {badge}
                </span>
              </div>}

            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-6" data-id="element-133">
              {h1}
            </h1>

            <p className="text-lg md:text-xl text-steel mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-134">
              {subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16" data-id="element-135">
              <button className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-136">
                Talk to MR
              </button>
            </div>

            {/* Animated Stats Row */}
            {stats && stats.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 border-t border-white/10" data-id="element-137">
                {stats.map((stat, i) => <div key={i} className="flex flex-col items-center" data-id="element-138">
                    <AnimatedCounter value={stat.value} data-id="element-139" />
                    <div className="text-sm text-steel mt-2 uppercase tracking-wider font-semibold" data-id="element-140">
                      {stat.label}
                    </div>
                  </div>)}
              </motion.div>}
          </motion.div>
        </div>
      </section>

      {/* Custom Content Section */}
      {customContent && <section className="relative z-20 -mt-12 mb-24 px-6" data-id="element-141">
          <div className="max-w-7xl mx-auto" data-id="element-142">{customContent}</div>
        </section>}

      {/* Problem Section */}
      {problem && <section className="py-24 px-6 bg-dark-bg relative" data-id="element-143">
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple/10 rounded-full blur-[150px] opacity-40" data-id="element-144" />

          <div className="inner-container relative z-10" data-id="element-145">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-146">
              <span className="eyebrow" data-id="element-147">The Problem</span>
              <h2 className="h2-title" data-id="element-148">{problem.subtitle}</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center" data-id="element-149">
              {problem.cards.map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.15
          }} className="group bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-xl p-8 shadow-lg hover:border-purple/40 hover:shadow-[0_0_30px_rgba(124,58,237,0.15)] transition-all duration-300" data-id="element-150">
                  <div className="text-teal mb-4" data-id="element-151">{card.icon}</div>
                  <h3 className="font-poppins font-bold text-xl mb-3" data-id="element-152">
                    {card.title}
                  </h3>
                  <p className="text-steel leading-relaxed" data-id="element-153">{card.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>}

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-154" />

      {/* How It Works */}
      {howItWorks && howItWorks.steps.length > 0 && <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-155">
          <div className="inner-container relative z-10" data-id="element-156">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-157">
              <span className="eyebrow" data-id="element-158">How It Works</span>
              <h2 className="h2-title" data-id="element-159">Say It. Done.</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-12 relative max-w-5xl mx-auto" data-id="element-160">
              {/* Animated Connector Line */}
              <div className="hidden md:block absolute top-16 left-[15%] right-[15%] h-1 overflow-hidden rounded-full" data-id="element-161">
                <div className="h-full bg-gradient-to-r from-purple via-teal to-purple opacity-30" data-id="element-162" />
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent" initial={{
              x: '-100%'
            }} animate={{
              x: '200%'
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-163" />
              </div>

              {howItWorks.steps.map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-164">
                  {/* Gradient Circle */}
                  <div className="relative w-32 h-32 mb-6" data-id="element-165">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple to-teal p-[3px] shadow-[0_0_30px_rgba(124,58,237,0.3)]" data-id="element-166">
                      <div className="w-full h-full rounded-full bg-navy-mid flex items-center justify-center" data-id="element-167">
                        <span className="text-4xl font-poppins font-bold bg-gradient-to-br from-purple-300 to-teal-300 bg-clip-text text-transparent" data-id="element-168">
                          {i + 1}
                        </span>
                      </div>
                    </div>
                  </div>

                  <h3 className="font-poppins font-bold text-xl mb-3" data-id="element-169">
                    {step.title}
                  </h3>
                  <p className="text-steel leading-relaxed" data-id="element-170">{step.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>}

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-171" />

      {/* Features */}
      {features && features.cards.length > 0 && <section className="section-padding bg-navy-dark relative overflow-hidden" data-id="element-172">
          {/* Ambient Glow */}
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-teal/10 rounded-full blur-[120px] opacity-30" data-id="element-173" />

          <div className="inner-container relative z-10" data-id="element-174">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-175">
              <span className="eyebrow" data-id="element-176">Capabilities</span>
              <h2 className="h2-title" data-id="element-177">Everything You Need.</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center" data-id="element-178">
              {features.cards.map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-purple/40 transition-all duration-300 overflow-hidden" data-id="element-179">
                  {/* Shimmer Effect on Hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-180">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-181" />
                  </div>

                  <div className="relative z-10" data-id="element-182">
                    <div className="flex items-center gap-4 mb-4" data-id="element-183">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple/20 to-teal/20 flex items-center justify-center text-purple group-hover:scale-110 transition-transform duration-300" data-id="element-184">
                        {card.icon}
                      </div>
                      <h3 className="font-poppins font-bold text-lg" data-id="element-185">
                        {card.title}
                      </h3>
                    </div>
                    <p className="text-steel text-sm leading-relaxed" data-id="element-186">
                      {card.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>

            {/* Callouts */}
            {(pricingCallout || demoCallout || firstClientCallout || integrations) && <div className="mt-16 grid gap-6 max-w-4xl mx-auto" data-id="element-187">
                {integrations && <div className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-teal/30 rounded-xl p-8 text-center relative overflow-hidden" data-id="element-188">
                    <div className="absolute inset-0 bg-teal/5 blur-xl" data-id="element-189" />
                    <div className="relative z-10" data-id="element-190">
                      <h4 className="font-poppins font-bold text-lg mb-6" data-id="element-191">
                        Works With Your Stack
                      </h4>
                      <div className="flex flex-wrap justify-center gap-3" data-id="element-192">
                        {integrations.tools.map(tool => <span key={tool} className="px-5 py-2.5 rounded-full bg-dark-bg border border-white/10 text-sm font-medium text-steel hover:border-teal/40 hover:shadow-[0_0_20px_rgba(7,133,183,0.15)] transition-all duration-300" data-id="element-193">
                            {tool}
                          </span>)}
                      </div>
                    </div>
                  </div>}

                {pricingCallout && <div className="bg-purple/10 border border-purple/30 rounded-xl p-6 text-center" data-id="element-194">
                    <p className="text-purple-alt font-medium" data-id="element-195">
                      {pricingCallout}
                    </p>
                  </div>}

                {demoCallout && <div className="bg-teal/10 border border-teal/30 rounded-xl p-6 text-center" data-id="element-196">
                    <p className="text-teal font-medium" data-id="element-197">💡 {demoCallout}</p>
                  </div>}

                {firstClientCallout && <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center" data-id="element-198">
                    <p className="text-white/80 font-medium" data-id="element-199">
                      🏆 {firstClientCallout}
                    </p>
                  </div>}
              </div>}
          </div>
        </section>}

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-200" />

      {/* Final CTA */}
      <section className="section-padding bg-hero-gradient relative overflow-hidden" data-id="element-201">
        {/* Animated Gradient Mesh Background */}
        <div className="absolute inset-0" data-id="element-202">
          <div className="absolute top-0 left-0 w-full h-full bg-dark-bg/50" data-id="element-203" />
          <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple/30 rounded-full blur-[100px]" animate={{
          x: [0, 50, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-204" />
          <motion.div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal/20 rounded-full blur-[100px]" animate={{
          x: [0, -40, 0],
          y: [0, -50, 0]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-205" />
        </div>

        {/* Floating Particles */}
        {[...Array(4)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 bg-white/20 rounded-full" style={{
        left: `${20 + i * 20}%`,
        top: `${30 + i * 15}%`
      }} animate={{
        y: [0, -30, 0],
        opacity: [0.2, 0.5, 0.2]
      }} transition={{
        duration: 3 + i,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.5
      }} data-id="element-206" />)}

        <div className="inner-container relative z-10 text-center max-w-3xl mx-auto" data-id="element-207">
          <h2 className="h2-title" data-id="element-208">Ready to get your life back?</h2>
          <p className="text-xl text-steel mb-10" data-id="element-209">
            MIndSTReam.ing — Your business, running itself.
          </p>
          <button className="btn-primary text-lg px-10 py-5 shadow-[0_0_40px_rgba(124,58,237,0.4)] hover:shadow-[0_0_60px_rgba(124,58,237,0.6)] transition-shadow" data-id="element-210">
            Talk to MR Now →
          </button>
          <p className="mt-6 text-sm text-steel" data-id="element-211">
            No contracts. No setup fees. Strategy call included.
          </p>
        </div>
      </section>
    </main>;
}